import {
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  type AdsConsent,
  adsEnabled,
  consentState,
  openConsentSettings,
  setConsent,
  subscribeConsent,
  takeReturnFocus,
} from '../analytics/google-ads';
import { buttonClass, Container } from './ui';

export function useConsent() {
  return useSyncExternalStore(subscribeConsent, consentState, consentState);
}

/**
 * While the banner is open, reserve its height at the bottom of the page:
 * body padding so the footer can scroll clear of it, and scroll padding so a
 * control that receives keyboard focus is scrolled above it rather than left
 * underneath (WCAG 2.2 2.4.11, Focus Not Obscured).
 */
function useReserveBottom(ref: RefObject<HTMLElement | null>, on: boolean) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!on || !el) return;
    const root = document.documentElement;
    const apply = () => {
      const h = `${el.offsetHeight}px`;
      root.style.scrollPaddingBottom = h;
      document.body.style.paddingBottom = h;
    };
    apply();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.scrollPaddingBottom = '';
      document.body.style.paddingBottom = '';
    };
  }, [ref, on]);
}

/**
 * The ad-cookie choice, fixed to the bottom of the screen. It never covers the
 * focused control or the end of the page (`useReserveBottom`). It is first in
 * the DOM after the skip link, so a keyboard user reaches it on their second
 * Tab even though it sits at the bottom. Accept and Reject carry equal weight.
 *
 * It doesn't take focus when it first appears — it isn't modal, and the
 * visitor came for the page. Opened from the footer, it does, and gives focus
 * back to the footer once a choice is made.
 */
export function ConsentBanner() {
  const { open } = useConsent();
  const [announcement, setAnnouncement] = useState('');
  const sectionRef = useRef<HTMLElement>(null);
  const showing = open && adsEnabled();
  useReserveBottom(sectionRef, showing);

  if (!adsEnabled()) return null;

  function choose(choice: AdsConsent) {
    setConsent(choice);
    setAnnouncement(
      choice === 'granted'
        ? 'Ad measurement cookies accepted.'
        : 'Ad measurement cookies rejected.',
    );
    const back = takeReturnFocus();
    // The buttons are about to unmount; don't leave focus on nothing.
    requestAnimationFrame(() => {
      const main = document.getElementById('main');
      (back ?? main)?.focus({ preventScroll: !back });
    });
  }

  return (
    <>
      {/* Always mounted, so the confirmation is announced after the banner
          itself is gone. */}
      <p aria-live="polite" className="sr-only" role="status">
        {announcement}
      </p>
      {showing ? (
        <section
          aria-labelledby="consent-title"
          className="fixed inset-x-0 bottom-0 z-40 border-t border-hairline-alt bg-surface shadow-soft"
          ref={sectionRef}
        >
          <Container className="flex flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between">
            <div className="max-w-3xl">
              <h2
                className="text-ui-strong font-semibold text-text-primary"
                id="consent-title"
                tabIndex={-1}
              >
                Cookies for ad measurement
              </h2>
              <p
                className="mt-1 text-ui text-text-secondary"
                id="consent-description"
              >
                With your OK, Google Ads sets cookies so we can tell which of
                our ads bring people here. Choose Reject and it won&rsquo;t. You
                can change this at any time from &ldquo;Cookie settings&rdquo;
                at the bottom of the page.
              </p>
            </div>
            <div className="flex shrink-0 gap-3">
              <button
                aria-describedby="consent-description"
                className={buttonClass('secondary', 'sm')}
                onClick={() => choose('denied')}
                type="button"
              >
                Reject
              </button>
              <button
                aria-describedby="consent-description"
                className={buttonClass('secondary', 'sm')}
                onClick={() => choose('granted')}
                type="button"
              >
                Accept
              </button>
            </div>
          </Container>
        </section>
      ) : null}
    </>
  );
}

/** The footer's way back to the choice. */
export function ConsentSettingsLink({ className }: { className?: string }) {
  if (!adsEnabled()) return null;
  return (
    <button
      className={className}
      onClick={() => {
        openConsentSettings();
        // Focus the banner's heading so a screen reader starts there.
        requestAnimationFrame(() =>
          document.getElementById('consent-title')?.focus(),
        );
      }}
      type="button"
    >
      Cookie settings
    </button>
  );
}
